import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import { useAddNotificationRuleMutation } from "../../../features/tenant/notificationRule/NotificationRuleApi";
import { useGetAllNotificationTemplatesMutation } from "../../../features/tenant/notificationTemplate/NotificationTemplateApi";
import toast from "react-hot-toast";

const AddNotificationRule = () => {
    const navigate = useNavigate();

    const [notificationId, setNotificationId] = useState("");
    const [ruleType, setRuleType] = useState("");
    const [frequencyType, setFrequencyType] = useState("");
    const [frequencyValue, setFrequencyValue] = useState("");
    const [ruleConfigValue, setRuleConfigValue] = useState("");
    const [selectedUserTypes, setSelectedUserTypes] = useState([]);
    const [notificationList, setNotificationList] = useState([]);

    // Get user types from session storage
    const appUserTypes = JSON.parse(sessionStorage.getItem("appUserType")) || [];

    const [
        AddNotificationRule,
        {
            data: AddNotificationRuleData,
            isLoading: AddNotificationRuleIsLoading,
            error: AddNotificationRuleError,
        },
    ] = useAddNotificationRuleMutation();

    const [
        GetAllNotificationTemplates,
        { data: GetAllNotificationTemplatesData },
    ] = useGetAllNotificationTemplatesMutation();

    // Fetch notifications when rule type is selected
    useEffect(() => {
        if (ruleType) {
            GetAllNotificationTemplates({
                token: sessionStorage.getItem("access_token"),
                tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
                body: {
                    limit: 1000,
                    offset: 0,
                    type: ruleType, // Send EVENT or FREQUENCY
                },
            });
        }
    }, [ruleType]);

    useEffect(() => {
        if (GetAllNotificationTemplatesData) {
            setNotificationList(GetAllNotificationTemplatesData.body.data || []);
        }
    }, [GetAllNotificationTemplatesData]);

    // Auto-select frequency type from notification's sub_type
    useEffect(() => {
        if (notificationId && ruleType === "FREQUENCY") {
            const selectedNotification = notificationList.find((n) => n.id === parseInt(notificationId));
            if (selectedNotification?.sub_type) {
                setFrequencyType(selectedNotification.sub_type);
            }
        }
    }, [notificationId, notificationList, ruleType]);

    useEffect(() => {
        if (AddNotificationRuleData) {
            toast.dismiss();
            toast.success(AddNotificationRuleData.message || "Rule added successfully");
            navigate(-1);
        }
        if (AddNotificationRuleError) {
            toast.dismiss();
            toast.error(AddNotificationRuleError?.data?.message || "Failed to add rule");
        }
    }, [AddNotificationRuleData, AddNotificationRuleError]);

    const handleUserTypeToggle = (userTypeValue) => {
        setSelectedUserTypes((prev) => {
            if (prev.includes(userTypeValue)) {
                return prev.filter((val) => val !== userTypeValue);
            } else {
                return [...prev, userTypeValue];
            }
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Validation
        if (!ruleType) {
            toast.error("Please select a rule type");
            return;
        }
        if (!notificationId) {
            toast.error("Please select a notification");
            return;
        }
        if (ruleType === "FREQUENCY") {
            if (!frequencyValue) {
                toast.error("Please enter frequency value");
                return;
            }
            if (!ruleConfigValue) {
                toast.error("Please enter rule config value (from last days)");
                return;
            }
        }
        if (selectedUserTypes.length === 0) {
            toast.error("Please select at least one user type");
            return;
        }

        // Get selected notification's notification_type
        const selectedNotification = notificationList.find((n) => n.id == parseInt(notificationId));

        const body = {
            notification_id: parseInt(notificationId),
            rule_type: ruleType,
            user_types: selectedUserTypes,
        };

        // Add rule_config only for FREQUENCY type
        if (ruleType === "FREQUENCY") {
            body.rule_config = {
                type: selectedNotification?.notification_type || "",
                value: parseInt(ruleConfigValue),
            };
        } else {
            body.rule_config = {
                event: selectedNotification?.notification_type || "",
            };
        }

        // Add frequency fields only if FREQUENCY is selected
        if (ruleType === "FREQUENCY") {
            body.frequency_type = selectedNotification.sub_type;
            body.frequency_value = parseInt(frequencyValue);
        }

        AddNotificationRule({
            token: sessionStorage.getItem("access_token"),
            tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
            body: body,
        });
    };

    return (
        <>
            <div className="flex w-full bg-gray-200 h-full justify-center">
                <div className="w-full">
                    <div className="border-b bg-white border-gray-300 pl-6 py-2 shadow-sm text-xl font-bold">
                        Add Notification Rule
                        <LinksComponent />
                    </div>
                    <div className="p-3">
                        <form className="p-3 bg-white shadow rounded-lg">
                            <div className="w-full flex flex-wrap gap-3">
                                {/* Rule Type - Show First */}
                                <div className="mb-6 flex-1 min-w-[250px] max-w-[400px]">
                                    <label className="block text-sm font-medium leading-6 text-gray-900 mb-1">
                                        Rule Type <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        className="shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 h-[42px]"
                                        value={ruleType}
                                        onChange={(e) => {
                                            setRuleType(e.target.value);
                                            // Reset all other fields when rule type changes
                                            setNotificationId("");
                                            setFrequencyType("");
                                            setFrequencyValue("");
                                            setRuleConfigValue("");
                                            setSelectedUserTypes([]);
                                            setNotificationList([]);
                                        }}
                                    >
                                        <option value="">Select Rule Type</option>
                                        <option value="EVENT">Event</option>
                                        <option value="FREQUENCY">Frequency</option>
                                    </select>
                                </div>

                                {/* Show remaining fields only after rule type is selected */}
                                {ruleType && (
                                    <>
                                        {/* Notification Selection */}
                                        <div className="mb-6 flex-1 min-w-[250px] max-w-[400px]">
                                            <label className="block text-sm font-medium leading-6 text-gray-900 mb-1">
                                                Notification Name <span className="text-red-500">*</span>
                                            </label>
                                            <select
                                                className="shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 h-[42px]"
                                                value={notificationId}
                                                onChange={(e) => setNotificationId(e.target.value)}
                                            >
                                                <option value="">Select Notification</option>
                                                {notificationList.map((notification) => (
                                                    <option key={notification.id} value={notification.id}>
                                                        {notification.notification_type}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Frequency Type is auto-selected from notification's sub_type */}

                                        {/* Frequency Value - Only show if FREQUENCY selected */}
                                        {ruleType === "FREQUENCY" && (
                                            <div className="mb-6 flex-1 min-w-[250px] max-w-[400px]">
                                                <label className="block text-sm font-medium leading-6 text-gray-900 mb-1">
                                                    Frequency Value <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="number"
                                                    className="shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                                                    placeholder="Enter frequency value"
                                                    value={frequencyValue}
                                                    onChange={(e) => setFrequencyValue(e.target.value)}
                                                    min="1"
                                                />
                                            </div>
                                        )}

                                        {/* Rule Config Value (From Last Days) - Only show for FREQUENCY */}
                                        {ruleType === "FREQUENCY" && (
                                            <div className="mb-6 flex-1 min-w-[250px] max-w-[400px]">
                                                <label className="block text-sm font-medium leading-6 text-gray-900 mb-1">
                                                    From Last Days <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="number"
                                                    className="shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                                                    placeholder="Enter number of days"
                                                    value={ruleConfigValue}
                                                    onChange={(e) => setRuleConfigValue(e.target.value)}
                                                    min="1"
                                                />
                                            </div>
                                        )}

                                        {/* User Types - Multi Select */}
                                        <div className="mb-6 w-full">
                                            <label className="block text-sm font-medium leading-6 text-gray-900 mb-1">
                                                User Types <span className="text-red-500">*</span>
                                            </label>
                                            <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3 bg-gray-50">
                                                {appUserTypes.map((userType) => (
                                                    <label
                                                        key={userType.user_type_id}
                                                        className="flex items-center cursor-pointer hover:bg-gray-100 p-2 rounded"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedUserTypes.includes(userType.user_type)}
                                                            onChange={() => handleUserTypeToggle(userType.user_type)}
                                                            className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                        />
                                                        <span className="text-sm text-gray-700 capitalize">
                                                            {userType.user_type}
                                                        </span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Submit Buttons */}
                            <div className="flex justify-center mt-4">
                                <button
                                    type="submit"
                                    onClick={handleSubmit}
                                    disabled={AddNotificationRuleIsLoading}
                                    className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center disabled:bg-blue-400"
                                >
                                    {AddNotificationRuleIsLoading ? "Adding..." : "Add"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AddNotificationRule;
